import { NgTemplateOutlet } from '@angular/common';
import {
  Component,
  computed,
  ElementRef,
  input,
  model,
  output,
  signal,
  viewChild,
  ChangeDetectionStrategy,
} from '@angular/core';
import { FormsModule } from '@angular/forms';
import { MatFormField, MatLabel } from '@angular/material/form-field';
import { MatIcon } from '@angular/material/icon';
import { MatInput } from '@angular/material/input';
import { TranslatePipe } from '@ngx-translate/core';
import { AiDisclosureComponent } from '@shared/components/ai-disclosure/ai-disclosure.component';
import { InputWithCounterComponent } from '@shared/components/input-with-counter/input-with-counter.component';
import {
  FailedTranslation,
  FailedTranslationsByLanguage,
} from '@shared/components/translate-dialog/ai-translation-outcome';
import { FailedTranslationNoteComponent } from '@shared/components/translate-dialog/failed-translation-note.component';
import { AiUpsellPopoverDirective } from '@shared/directives/ai-upsell-popover.directive';
import { I18nSupportedLanguage } from '@shared/interfaces/i18n.interface';

/**
 * Stacked per-language translation list for a single translatable field:
 * the default language pinned on top, active languages as input rows and
 * inactive languages in a collapsible section.
 *
 * Used inside the translate dialog (read-only default) and embedded in
 * forms such as the pulse add/edit question dialog (editable default).
 */
@Component({
  selector: 'app-translation-language-list',
  templateUrl: './translation-language-list.component.html',
  styleUrl: './translation-language-list.component.scss',
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [
    FormsModule,
    MatFormField,
    MatLabel,
    MatInput,
    MatIcon,
    NgTemplateOutlet,
    AiDisclosureComponent,
    FailedTranslationNoteComponent,
    InputWithCounterComponent,
    AiUpsellPopoverDirective,
    TranslatePipe,
  ],
})
export class TranslationLanguageListComponent {
  /** Reference language pinned at the top; null hides the default section. */
  readonly defaultLanguage = input<I18nSupportedLanguage | null>(null);
  /** Active languages to translate (default language excluded). */
  readonly languages = input.required<I18nSupportedLanguage[]>();
  /** Languages shown in the collapsible inactive section. */
  readonly inactiveLanguages = input<I18nSupportedLanguage[]>([]);
  /** Translated values per language code (default language excluded). */
  readonly values = model.required<Record<string, string>>();
  /** Default-language value: the source reference, editable via defaultEditable. */
  readonly defaultValue = model('');
  readonly defaultEditable = input(false);
  /** Placeholder for the editable default input. */
  readonly defaultPlaceholder = input('');
  /** Pre-translated field label, used in aria-labels. */
  readonly fieldLabel = input('');
  readonly maxLength = input<number | undefined>(undefined);
  /** Language codes whose inputs are locked (may include the default language). */
  readonly readonlyLangs = input<string[]>([]);
  /** Optional per-language stem shown as each row's floating label (e.g. the scale introduction). */
  readonly stemByCode = input<Record<string, string>>({});
  /** Language codes freshly filled by AI — badged until the user edits the row. */
  readonly aiTranslatedCodes = input<string[]>([]);
  /** Languages the last AI suggestion left unfilled, with the reason — never counted as complete. */
  readonly failedTranslations = input<FailedTranslationsByLanguage>({});
  /** data-testid per input: `{prefix}-{langCode}` or `{prefix}-{langCode}-{suffix}`. */
  readonly testIdPrefix = input('input-translation');
  readonly testIdSuffix = input('');
  /** Whether an AI suggestion request is in flight — disables the suggest button. */
  readonly isSuggesting = input(false);
  /** Consumer gate for the suggest button (e.g. AI endpoint configured). */
  readonly canSuggest = input(true);
  /** Whether the account has Foodback Intelligence; when false the suggest button is disabled with a tooltip. */
  readonly aiAvailable = input(true);

  /** Emitted when the user clicks the "Suggest all" button. */
  readonly suggestAll = output<void>();

  /** Suggest is only possible with AI available and a non-empty source to translate from. */
  protected readonly suggestDisabled = computed(
    () =>
      this.isSuggesting() ||
      !this.canSuggest() ||
      !this.aiAvailable() ||
      !this.defaultValue().trim()
  );

  protected readonly inactiveExpanded = signal(false);

  /** True when the language has a translation, there is a source to translate and nothing failed. */
  protected readonly completionMap = computed<Record<string, boolean>>(() => {
    const hasSource = this.defaultValue().trim().length > 0;
    const failed = this.failedTranslations();
    const map: Record<string, boolean> = {};
    for (const lang of [...this.languages(), ...this.inactiveLanguages()]) {
      map[lang.code] = hasSource && Boolean(this.values()[lang.code]?.trim()) && !failed[lang.code];
    }
    return map;
  });

  private readonly defaultInputRef = viewChild<unknown, ElementRef<HTMLElement>>('defaultInput', {
    read: ElementRef,
  });

  /** Moves focus to the editable default input — used by "Save & add another" flows. */
  focusDefaultInput(): void {
    this.defaultInputRef()?.nativeElement.querySelector<HTMLInputElement>('input')?.focus();
  }

  protected displayName(lang: I18nSupportedLanguage): string {
    return lang.name ?? lang.localizedLanguageName ?? lang.code;
  }

  protected isReadonly(code: string): boolean {
    return this.readonlyLangs().includes(code);
  }

  protected stemFor(code: string): string | null {
    return this.stemByCode()[code] ?? null;
  }

  protected isAiTranslated(code: string): boolean {
    return this.aiTranslatedCodes().includes(code);
  }

  protected failureFor(code: string): FailedTranslation | null {
    return this.failedTranslations()[code] ?? null;
  }

  protected setValue(code: string, value: string): void {
    this.values.update(current => ({ ...current, [code]: value }));
  }

  protected noteIdFor(code: string): string {
    return `${this.testIdFor(code)}-failure`;
  }

  protected testIdFor(code: string): string {
    const suffix = this.testIdSuffix();
    return `${this.testIdPrefix()}-${code}${suffix ? '-' + suffix : ''}`;
  }
}
